import { useEffect, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { KnowledgeSkeleton } from "@/components/PageSkeletons";

export default function KnowledgePage() {
  const [focus, setFocus] = useState(null);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 900, h: 620 });
  const fgRef = useRef(null);
  // Resolve design tokens to hex once — canvas doesn't accept var(--…).
  const [palette, setPalette] = useState({
    bg: "#FFFFFF",
    accent: "#0FA9DB",
    accentSoft: "#DBEDF7",
    fg: "#0F1B2D",
    link: "rgba(15, 169, 219, 0.18)",
  });

  useEffect(() => {
    const s = getComputedStyle(document.documentElement);
    const bg = s.getPropertyValue("--hm-bg").trim() || "#FFFFFF";
    const accent = s.getPropertyValue("--hm-accent").trim() || "#0FA9DB";
    const soft = s.getPropertyValue("--hm-accent-soft").trim() || "#DBEDF7";
    const fg = s.getPropertyValue("--hm-fg").trim() || "#0F1B2D";
    const glow = s.getPropertyValue("--hm-accent-glow").trim();
    setPalette({
      bg, accent, accentSoft: soft, fg,
      link: glow || "rgba(15, 169, 219, 0.18)",
    });
  }, []);

  useEffect(() => {
    fetch(`${API}/user/events`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ event_name: "knowledge_visited", payload: {} }),
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (el) setSize({ w: el.clientWidth, h: Math.max(520, window.innerHeight - 260) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const { data: graphResp, isPending } = useQuery({
    queryKey: qk.knowledge({ limit: 300, focus }),
    queryFn: () => {
      const p = new URLSearchParams({ limit: "300" });
      if (focus) { p.set("focus", focus); p.set("depth", "2"); }
      return fetch(`${API}/knowledge/graph?${p}`, { headers: authHeaders() })
        .then((r) => r.ok ? r.json() : Promise.reject(new Error("http " + r.status)));
    },
  });

  const graph = graphResp
    ? {
        nodes: graphResp.nodes.map((n) => ({ ...n })),
        edges: graphResp.edges.map((e) => ({
          source: e.source, target: e.target, relation: e.relation, weight: e.weight,
        })),
      }
    : { nodes: [], edges: [] };

  return (
    <div data-testid="knowledge-page">
      <PageHeader title="Knowledge" badge="Live" subtitle="Companies, topics, sources — live relationships. Click a node to focus." />
      {isPending || !graphResp ? (
        <KnowledgeSkeleton />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
        <div ref={wrapRef} className="hm-surface overflow-hidden" data-testid="knowledge-graph" style={{ height: size.h }}>
          <ForceGraph2D
            ref={fgRef}
            graphData={{ nodes: graph.nodes, links: graph.edges }}
            width={size.w}
            height={size.h}
            backgroundColor={palette.bg}
            nodeRelSize={5}
            nodeColor={(n) => n.color || palette.accent}
            nodeVal={(n) => n.priority === "critical" ? 10 : n.priority === "high" ? 6 : 4}
            linkColor={() => palette.link}
            linkDirectionalParticles={0}
            nodeCanvasObject={(node, ctx, globalScale) => {
              const r = node.priority === "critical" ? 6 : node.priority === "high" ? 5 : 4;
              ctx.fillStyle = node.color || palette.accent;
              ctx.beginPath(); ctx.arc(node.x, node.y, r, 0, 2 * Math.PI); ctx.fill();
              if (globalScale > 1.2) {
                ctx.fillStyle = palette.fg;
                ctx.font = "11px Inter, system-ui, sans-serif";
                ctx.textAlign = "left"; ctx.textBaseline = "middle";
                ctx.fillText(node.label, node.x + r + 4, node.y);
              }
            }}
            onNodeClick={(n) => setSelected(n)}
          />
        </div>
        <aside className="hm-surface p-5 h-fit" data-testid="knowledge-panel">
          {selected ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2 w-2 rounded-full" style={{ background: selected.color }} />
                <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">{selected.kind}</span>
              </div>
              <h3 className="text-lg font-medium text-hm-fg">{selected.label}</h3>
              {selected.priority && (
                <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted mt-1">priority · {selected.priority}</div>
              )}
              <button onClick={() => setFocus(selected.id)} data-testid="focus-node-btn"
                className="mt-6 w-full h-9 rounded-md bg-hm-accent text-hm-bg hover:bg-hm-accent-dim text-sm font-medium">
                Focus this node
              </button>
              {focus === selected.id && (
                <button onClick={() => { setFocus(null); setSelected(null); }}
                  className="mt-2 w-full h-9 rounded-md border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong text-sm">
                  Clear focus
                </button>
              )}
            </>
          ) : (
            <>
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-2">graph</div>
              <div className="text-sm text-hm-fg">{graph.nodes.length} nodes · {graph.edges.length} edges</div>
              <p className="text-xs text-hm-fg-muted mt-3 leading-relaxed">Click any node in the canvas to focus and expand its neighbourhood.</p>
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2 mono text-[11px] text-hm-fg-muted"><span className="h-2 w-2 rounded-full bg-hm-accent" />companies</div>
                <div className="flex items-center gap-2 mono text-[11px] text-hm-fg-muted"><span className="h-2 w-2 rounded-full" style={{background:"#C4A6FF"}} />topics</div>
                <div className="flex items-center gap-2 mono text-[11px] text-hm-fg-muted"><span className="h-2 w-2 rounded-full" style={{background:"#7BB8F5"}} />sources</div>
              </div>
            </>
          )}
        </aside>
      </div>
      )}
    </div>
  );
}
